import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import PageHeader from "components/PageLayout";
import Skeleton from "components/Skeleton";
import EmptyState from "components/EmptyState";
import RelativeTime from "components/RelativeTime";
import { requireAuth, useAuth } from "utilities/auth";
import { useEffectiveCompanyId } from "hooks/useEffectiveCompanyId";
import { makeApiRequest, apiRoutes } from "constants/apiRoutes";
import { useToast } from "components/Toast";
import { useTicketTemplates } from "hooks/useTicketTemplates";
import { useRequestTypesAdmin } from "hooks/useRequestTypesAdmin";
import { TEMPLATE_STATUS } from "constants/ticketTemplates";
import { groupWorkflows, describeTemplateError } from "utilities/workflowVariants";
import TemplateErrorPanel from "components/Workflows/TemplateErrorPanel";
import { Workflow, Plus, Search, Star } from "lucide-react";

/**
 * WorkflowList (SOUND-506 → SOUND-785) — the request-type CATALOG, now with
 * VARIANTS. One block per request type (so the page still doubles as a coverage
 * view: which types lack a flow); inside it, one line per variant with its live
 * version, a Default badge, and any drafts. A type may have several live
 * variants; exactly one is its default, which is what a ticket runs when nobody
 * picks (clients, Telegram, and every caller that sends no templateId).
 *
 * Labels come from the ADMIN vocabulary (includeInactive), so a workflow on a
 * since-deactivated type still reads by its name, not a raw key.
 */
function WorkflowList() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const companyId = useEffectiveCompanyId();
  const { toast } = useToast();
  const { templates, loading: templatesLoading, refetch } = useTicketTemplates();
  const { requestTypes, loading: rtLoading } = useRequestTypesAdmin({ includeInactive: true });
  const [search, setSearch] = useState("");
  const [busyId, setBusyId] = useState(null);
  // The last refused action, shown until dismissed — never only a toast.
  const [actionError, setActionError] = useState(null);

  const groups = useMemo(() => {
    const q = search.trim().toLowerCase();
    return groupWorkflows(requestTypes, templates)
      .filter(
        (g) =>
          !q ||
          g.label.toLowerCase().includes(q) ||
          g.key.toLowerCase().includes(q) ||
          g.variants.some((v) => v.variantLabel.toLowerCase().includes(q))
      )
      .sort((a, b) => {
        if (a.active !== b.active) return a.active ? -1 : 1;
        return a.label.localeCompare(b.label);
      });
  }, [requestTypes, templates, search]);

  const loading = templatesLoading || rtLoading;

  const makeDefault = async (variant) => {
    if (!variant.live) return;
    setBusyId(variant.live.templateId);
    setActionError(null);
    try {
      await makeApiRequest(
        "post",
        apiRoutes.makeDefaultTicketTemplate(variant.live.templateId),
        { id: user.id, accessToken: user.accessToken },
        companyId
      );
      toast({ status: "success", title: `"${variant.variantLabel}" is now the default` });
      refetch();
    } catch (err) {
      setActionError(describeTemplateError(err, "Could not change the default"));
    } finally {
      setBusyId(null);
    }
  };

  const addVariant = (g) =>
    navigate(`/settings/workflows/new?requestType=${encodeURIComponent(g.key)}&newVariant=1`);

  return (
    <div>
      <PageHeader
        title="Ticket Workflows"
        subtitle="Structured intake + an auto-generating child-ticket flow, per request type. A type can have several variants; its default runs whenever nobody picks one."
        actions={
          <button className="btn btn-primary" onClick={() => navigate("/settings/workflows/new")}>
            <Plus className="h-4 w-4" />
            New workflow
          </button>
        }
      />

      <div className="container space-y-4 py-6">
        <TemplateErrorPanel error={actionError} onDismiss={() => setActionError(null)} />

        <div className="relative max-w-sm">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            className="input pl-9"
            placeholder="Search request types or variants…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {loading ? (
          <div className="space-y-3">
            <Skeleton.Rect className="h-12 w-full" />
            <Skeleton.Rect className="h-12 w-full" />
            <Skeleton.Rect className="h-12 w-2/3" />
          </div>
        ) : groups.length === 0 ? (
          <EmptyState icon={Workflow} heading="No request types match your search" />
        ) : (
          <div className="surface divide-y divide-border overflow-hidden rounded-xl ring-1 ring-border">
            {groups.map((g) => {
              const st = TEMPLATE_STATUS[g.status];
              return (
                <div key={g.key} className="px-4 py-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium text-foreground">{g.label}</span>
                        <span className={`badge ${st.badge}`}>
                          {g.status === "active" && g.liveCount > 1 ? `${g.liveCount} live variants` : st.label}
                        </span>
                        {!g.active && <span className="badge badge-muted">Type deactivated</span>}
                      </div>
                      <div className="font-mono text-xs text-muted-foreground">{g.key}</div>
                    </div>
                    {g.active &&
                      (g.variants.length === 0 ? (
                        <button
                          className="btn btn-outline h-8 px-3 text-xs"
                          onClick={() =>
                            navigate(`/settings/workflows/new?requestType=${encodeURIComponent(g.key)}`)
                          }
                        >
                          <Plus className="h-3.5 w-3.5" />
                          Create workflow
                        </button>
                      ) : (
                        <button className="btn btn-outline h-8 px-3 text-xs" onClick={() => addVariant(g)}>
                          <Plus className="h-3.5 w-3.5" />
                          Add variant
                        </button>
                      ))}
                  </div>

                  {g.variants.length > 0 && (
                    <ul className="mt-2 space-y-1">
                      {g.variants.map((v) => (
                        <li key={v.variantKey} className="rounded-md ring-1 ring-border/60">
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 text-sm">
                            <button
                              className="flex min-w-0 flex-1 items-center gap-2 text-left hover:underline"
                              onClick={() => navigate(`/settings/workflows/${v.primary.templateId}`)}
                            >
                              <span className="truncate font-medium text-foreground">{v.variantLabel}</span>
                              <span className="font-mono text-[11px] text-muted-foreground">{v.variantKey}</span>
                            </button>
                            {v.isDefault && (
                              <span className="badge badge-brand inline-flex items-center gap-1">
                                <Star className="h-3 w-3" />
                                Default
                              </span>
                            )}
                            <span className={`badge ${v.live ? "badge-success" : "badge-muted"}`}>
                              {v.live ? `Live · v${v.live.version}` : v.drafts.length > 0 ? "Not live" : "Deactivated"}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {(v.primary.intakeFields || []).length} fields · {(v.primary.childSteps || []).length}{" "}
                              {(v.primary.childSteps || []).length === 1 ? "step" : "steps"}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {v.primary.updatedAt ? <RelativeTime value={v.primary.updatedAt} /> : "—"}
                            </span>
                            {v.live && !v.isDefault && (
                              <button
                                className="btn btn-ghost h-7 px-2 text-xs"
                                disabled={busyId === v.live.templateId}
                                onClick={() => makeDefault(v)}
                              >
                                Make default
                              </button>
                            )}
                          </div>
                          {v.drafts.length > 0 && (
                            <div className="flex flex-wrap items-center gap-2 border-t border-border/60 px-3 py-1.5 text-xs">
                              {v.drafts.map((d) => (
                                <button
                                  key={d.templateId}
                                  className="badge badge-warning hover:underline"
                                  onClick={() => navigate(`/settings/workflows/${d.templateId}`)}
                                  title={d.name || "Draft"}
                                >
                                  Draft{d.name ? ` · ${d.name}` : ""}
                                </button>
                              ))}
                              {v.retiredCount > 0 && (
                                <span className="text-muted-foreground">
                                  {v.retiredCount} earlier {v.retiredCount === 1 ? "version" : "versions"}
                                </span>
                              )}
                            </div>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default requireAuth(WorkflowList);
